import React, { useId, useMemo, useState } from 'react';
import RichText from '../richText/RichText.jsx';
import SentenceMap from '../sentenceMap/SentenceMap.jsx';
import QuizQuestion from '../practice/QuizQuestion.jsx';
import { isAnswerComplete } from '../practice/practiceHelpers.js';
import { SUPPLEMENT_PART_IDS, supplementPartLocation, supplementView } from './articleIntroductionSupplement.js';
import '../practiceActivities/practiceActivities.css';
import './articlePilot.css';
import './articleIntroductionSupplement.css';

const WRITING_FIELDS = [
    ['headline', 'Headline', 240],
    ['strapline', 'Strapline (optional)', 240],
    ['opening', 'Opening paragraph', 1800]
];

function Disclosure({ label, children }) {
    const id = useId();
    const [open, setOpen] = useState(false);
    return <details className="intro-disclosure supplement-disclosure" open={open} onToggle={event => setOpen(event.currentTarget.open)}>
        <summary aria-controls={id} aria-expanded={open}>{label}</summary>{open && <div id={id}>{children}</div>}
    </details>;
}

function TextList({ values = [] }) {
    return <ul className="supplement-list">{values.map((value, index) => <li key={index}><RichText text={value} /></li>)}</ul>;
}

function Tiles({ rows = [] }) {
    return <div className="supplement-tiles">{rows.map(([label, value], index) => <section className="supplement-tile" key={index}>
        <h4>{label}</h4><RichText text={value} />
    </section>)}</div>;
}

function CompleteExample({ value, label }) {
    return <div className="supplement-example selectable" aria-label={label}>
        <p className="supplement-badge">{label}</p>
        {WRITING_FIELDS.map(([field, title]) => <section key={field} className={'supplement-example-' + field}>
            <h4>{title}</h4>{value[field] ? <RichText text={value[field]} /> : <p className="supplement-note">Not used.</p>}
        </section>)}
    </div>;
}

function WritingFields({ moduleId, kind, drafts, onDraftChange }) {
    return <div className="supplement-writing selectable">{WRITING_FIELDS.map(([field, title, maxLength]) => {
        const key = moduleId + '_' + kind + '_' + field;
        return <label className="btl-activity-draft" key={field}>{title}
            <textarea rows={field === 'opening' ? 5 : 2} maxLength={maxLength}
                value={drafts[key] || ''} onChange={event => onDraftChange?.(key, event.target.value)} />
        </label>;
    })}</div>;
}

function CurrentQuestion({ value, newTask }) {
    return <section className="supplement-current-question selectable" data-question-kind={newTask ? 'new' : 'worked'} aria-label="Current article question">
        <p className="supplement-badge">{newTask ? 'Your turn · New question' : 'Worked example · Same question'}</p>
        <h3>{value.label}</h3>
        <p className="supplement-badge">Original practice question · Form: Article</p>
        <div className="supplement-question-text"><RichText text={value.question} /></div>
        <dl className="supplement-question-context"><div><dt>Reader</dt><dd>{value.audience}</dd></div><div><dt>Purpose</dt><dd>{value.purpose}</dd></div></dl>
    </section>;
}

function InlineQuestion({ value, id, state = {}, onChange }) {
    const [checked, setChecked] = useState(false);
    const q = useMemo(() => ({ id, type: 'mcq', question: value.question, options: value.options,
        correct: value.correctAnswer, explanation: value.explanation }), [value, id]);
    const picked = state.answers?.[id];
    const answer = Number.isInteger(picked) && picked >= 0 && picked < q.options.length ? picked : undefined;
    const update = (nextAnswer, nextChecked) => onChange?.({ ...state,
        answers: { ...state.answers, [id]: nextAnswer }, checked: { ...state.checked, [id]: nextChecked } });
    return <section className="supplement-inline-question selectable" aria-label="In-lesson check">
        <h4>{q.question}</h4>
        <QuizQuestion q={q} answer={answer} submitted={checked} collapsedHints
            setAnswer={next => { setChecked(false); update(next, false); }} />
        <div className="btl-activity-controls">{checked
            ? <button type="button" onClick={() => { setChecked(false); update(answer, false); }}>Try again</button>
            : <button type="button" disabled={!isAnswerComplete(q, answer)} onClick={() => {
                if (isAnswerComplete(q, answer)) { setChecked(true); update(answer, true); }
            }}>Check my choice</button>}</div>
    </section>;
}

function NativeMap({ maps, name }) {
    return maps[name] ? <div className="supplement-map selectable"><SentenceMap value={maps[name]} /></div> : null;
}

function GuidedWordplay({ value }) {
    const [shown, setShown] = useState(0);
    return <section className="supplement-guided">
        <p className="supplement-badge">{value.label}</p><RichText text={value.question} />
        {shown > 0 && <ol className="supplement-list">{value.hints.slice(0, shown).map((hint, index) => <li key={index}><RichText text={hint} /></li>)}</ol>}
        <button type="button" disabled={shown >= value.hints.length} onClick={() => setShown(count => Math.min(value.hints.length, count + 1))}>
            {shown >= value.hints.length ? 'All guiding questions shown' : 'Show guiding question ' + (shown + 1)}
        </button>
        <Disclosure label="Compare the two meanings"><RichText text={value.answer} /><Tiles rows={value.meanings} /><RichText text={value.why} /></Disclosure>
    </section>;
}

function SoundPractice({ module, state, onChange }) {
    const [index, setIndex] = useState(0);
    const questions = module.soundPractice || [];
    if (!questions[index]) return null;
    return <section aria-label="Sound checks"><p role="status">Sound check {index + 1} of {questions.length}</p>
        <InlineQuestion key={index} value={questions[index]} id={module.id + '_sound_' + index} state={state} onChange={onChange} />
        <nav className="btl-activity-controls" aria-label="Sound check questions">
            <button type="button" disabled={index === 0} onClick={() => setIndex(value => value - 1)}>Previous pair</button>
            <button type="button" disabled={index === questions.length - 1} onClick={() => setIndex(value => value + 1)}>Next pair</button>
        </nav>
    </section>;
}

function StyleComparison({ value }) {
    return <section className="supplement-style-comparison" aria-label="Style comparison">
        <p className="supplement-badge">{value.scope}</p>
        <div className="supplement-tiles">{[['weak', 'less-effective'], ['strong', 'more-precise']].map(([key, status]) => <section key={key}
            className="supplement-tile supplement-style-card" data-style={status}>
            <h4>{value[key].label}</h4><p className="supplement-badge">Grammatical heading</p>
            <RichText text={value[key].text} /><RichText text={value[key].remark} />
        </section>)}</div>
        <RichText text={value.question} />
    </section>;
}

function LanguageFocus({ module, maps, inlineState, onInlineStateChange }) {
    const language = module.language;
    return <section className="supplement-language" aria-label="Language focus">
        <h3>Language focus</h3><RichText text={language.idea} /><Tiles rows={language.tiles} />
        {module.sentenceComparison && <section className="supplement-comparison">
            <h4>Heading and opening: different jobs</h4>
            <Tiles rows={[[ 'Heading · Phrase allowed', module.sentenceComparison.heading ]]} />
            <NativeMap maps={maps} name="safe_opening" />
            <InlineQuestion value={module.sentenceComparison} id={module.id + '_heading'} state={inlineState} onChange={onInlineStateChange} />
        </section>}
        {module.placementComparison && <div className="supplement-tiles">{module.placementComparison.map((row, index) => <section className="supplement-tile" key={index}>
            <h4>{row.label}</h4><RichText text={row.text} /><RichText text={row.note} />
        </section>)}</div>}
        {module.id === 'pun' && <><NativeMap maps={maps} name="paired_frame" /><NativeMap maps={maps} name="paired_change" /></>}
        {module.guidedWordplay && <Disclosure label="Guided wordplay: an alternative headline for the same question"><GuidedWordplay value={module.guidedWordplay} /></Disclosure>}
        {module.id === 'allit' && <><NativeMap maps={maps} name="alliteration_frame" /><NativeMap maps={maps} name="alliteration_example" /></>}
        {language.grammar && <Disclosure label="Optional grammar check"><p className="supplement-badge">Grammar practice</p>
            {maps.alliteration_grammar ? <NativeMap maps={maps} name="alliteration_grammar" />
                : <><RichText text={language.grammar.error} /><Disclosure label="Show the grammatical version"><RichText text={language.grammar.correct} /></Disclosure></>}
            <Disclosure label="Grammar explanation"><RichText text={language.grammar.note} /></Disclosure>
        </Disclosure>}
        {module.soundBank && <Disclosure label={module.soundBank.length + ' sound-pair references'}>
            <div className="supplement-tiles">{module.soundBank.map((row, index) => <section className="supplement-tile" key={index}>
                <p className="supplement-badge">{row.sound}</p><h4>{row.pair}</h4><RichText text={row.note} />
            </section>)}</div>
        </Disclosure>}
        {module.soundPractice && <Disclosure label={'Try ' + module.soundPractice.length + ' sound checks'}>
            <SoundPractice module={module} state={inlineState} onChange={onInlineStateChange} />
        </Disclosure>}
        {module.styleComparison && <StyleComparison value={module.styleComparison} />}
    </section>;
}

function StageCard({ module, stepId, maps, drafts, onDraftChange, inlineState, onInlineStateChange, notes = [] }) {
    const first = Object.fromEntries(WRITING_FIELDS.map(([field]) => [field, drafts[module.id + '_first_' + field] || '']));
    const frameKey = module.id + '_frame';
    return <>
        {stepId === 'question' && <><RichText text={module.explanation} /><p>Read the full question above. Identify the reader, purpose and issue before moving to the complete worked example.</p></>}
        {stepId === 'model' && <Disclosure label="Show the complete worked example for this question">
            <CompleteExample value={module.model} label="Complete worked example · Same question" />
            {module.id === 'pun' && <NativeMap maps={maps} name="paired_change" />}
            {module.id === 'allit' && <NativeMap maps={maps} name="safe_opening" />}
        </Disclosure>}
        {stepId === 'notice' && <>
            <div className="supplement-tiles">{module.notice.map((row, index) => <section className="supplement-tile" key={index}>
                <h4>{row.label}</h4>{row.icon && <span className="supplement-meaning-icon" aria-hidden="true">{row.icon}</span>}
                <RichText text={row.text} /><RichText text={row.why} />
            </section>)}</div>
            <InlineQuestion value={module.noticeCheck} id={module.id + '_notice'} state={inlineState} onChange={onInlineStateChange} />
            <LanguageFocus module={module} maps={maps} inlineState={inlineState} onInlineStateChange={onInlineStateChange} />
        </>}
        {stepId === 'write' && <>
            <p>Use the new question above. Write one headline, an optional strapline and a short opening paragraph.</p>
            {module.framePractice && <section className="supplement-frame-practice selectable"><h3>First: one sentence using the same frame</h3>
                <RichText text={module.framePractice.question} />
                <label className="btl-activity-draft">My opening sentence<textarea rows={3} maxLength={500} value={drafts[frameKey] || ''}
                    onChange={event => onDraftChange?.(frameKey, event.target.value)} /></label>
                <Disclosure label="Step-by-step sentence hints"><TextList values={module.framePractice.hints} /></Disclosure>
            </section>}
            <WritingFields moduleId={module.id} kind="first" drafts={drafts} onDraftChange={onDraftChange} />
            <Disclosure label="Optional writing hints"><TextList values={module.hints} /></Disclosure>
        </>}
        {stepId === 'compare' && <>
            <p>Compare versions for the new question above. Keep your first draft, revise a choice and explain your reason.</p>
            {Object.values(first).some(value => value.trim())
                ? <Disclosure label="My first version"><CompleteExample value={first} label="My first draft · New question" /></Disclosure>
                : <p className="supplement-note">No first draft entered yet. You can return to Write, or study the example for support.</p>}
            {drafts[frameKey] && <Disclosure label="My frame sentence"><RichText text={drafts[frameKey]} /></Disclosure>}
            <Disclosure label="Show one possible answer for the new question">
                <CompleteExample value={module.answer} label="One possible answer · New question" />
                <TextList values={module.answerNotes} />
                {module.framePractice && <><NativeMap maps={maps} name="paired_transfer" /><RichText text={module.framePractice.why} /></>}
            </Disclosure>
            <Disclosure label="Revise my version"><TextList values={module.check} />
                <WritingFields moduleId={module.id} kind="revision" drafts={drafts} onDraftChange={onDraftChange} />
                <label className="btl-activity-draft">Explain one change<textarea rows={3} maxLength={600} value={drafts[module.id + '_reason'] || ''}
                    onChange={event => onDraftChange?.(module.id + '_reason', event.target.value)} /></label>
            </Disclosure>
        </>}
        {notes.length > 0 && <Disclosure label="Further study notes">{notes.map((note, index) => <RichText key={index} text={note} />)}</Disclosure>}
    </>;
}

export default function ArticleIntroductionSupplement({ lesson, partId, onPart, onReturn, onMain, onQuiz, quizAvailable = false,
    drafts = {}, onDraftChange, inlineState = {}, onInlineStateChange }) {
    const view = useMemo(() => supplementView(lesson), [lesson]);
    if (!view) return <p role="status">The Introduction supplement is not available here yet.</p>;
    const location = supplementPartLocation(partId) || supplementPartLocation(SUPPLEMENT_PART_IDS[0]);
    const module = view.modules[location.moduleIndex];
    const selected = SUPPLEMENT_PART_IDS[location.index];
    const step = view.steps[location.stepIndex];
    const activeQuestion = location.stepIndex < 3 ? module.model : module.practice;
    const notes = view.reviewNotes?.[module.id]?.[location.stepId] || [];
    return <section className="article-pilot article-introductions article-introduction-supplement" aria-label={view.meta.title}>
        <header><p className="article-pilot-eyebrow">Optional Introduction supplement</p><h2>{view.meta.title}</h2>
            <p className="article-pilot-storage">Drafts and practice choices stay in this page session. Copy your work before leaving or reloading.</p>
            <div className="btl-activity-controls"><button type="button" onClick={onReturn}>Back to Article route</button>
                <button type="button" onClick={onMain} disabled={!onMain}>Main Introduction lesson</button>
                <button type="button" onClick={onQuiz} disabled={!quizAvailable}>Supplement foundation practice · 22 questions / 30 responses</button></div>
            <Disclosure key={selected} label="How to use this supplement"><RichText text={view.meta.teachingNote} />{(view.reviewNotes?.overview || []).map((note, index) => <RichText key={index} text={note} />)}</Disclosure>
        </header>
        <nav className="supplement-modules" aria-label="Supplement modules">{view.modules.map((entry, index) => <button type="button" key={entry.id}
            aria-pressed={entry.id === module.id} onClick={() => onPart?.(SUPPLEMENT_PART_IDS[index * view.steps.length])}>{entry.title}</button>)}</nav>
        <nav className="supplement-steps" aria-label="Supplement stages">{view.steps.map((entry, index) => <button type="button" key={entry.id}
            aria-current={entry.id === location.stepId ? 'step' : undefined} onClick={() => onPart?.(SUPPLEMENT_PART_IDS[location.moduleIndex * view.steps.length + index])}>{entry.title}</button>)}</nav>
        <CurrentQuestion value={activeQuestion} newTask={location.stepIndex >= 3} />
        <section className="btl-activity supplement-stage" key={selected} data-supplement-part={selected} aria-label={step.title}>
            <p className="supplement-badge" role="status">{module.title} · Step {location.stepIndex + 1} of {view.steps.length}</p>
            <h3>{step.title}</h3><p className="supplement-technique">{module.technique}</p>
            <StageCard module={module} stepId={location.stepId} maps={view.maps} drafts={drafts} onDraftChange={onDraftChange}
                inlineState={inlineState} onInlineStateChange={onInlineStateChange} notes={notes} />
        </section>
        <nav className="btl-activity-controls supplement-navigation" aria-label="Supplement progression">
            <button type="button" disabled={location.index === 0} onClick={() => onPart?.(SUPPLEMENT_PART_IDS[location.index - 1])}>Previous stage</button>
            <button type="button" disabled={location.index === SUPPLEMENT_PART_IDS.length - 1} onClick={() => onPart?.(SUPPLEMENT_PART_IDS[location.index + 1])}>
                {location.stepIndex === view.steps.length - 1 ? 'Next module' : 'Next stage'}</button>
        </nav>
        {!quizAvailable && <p className="supplement-note" role="status">This supplement’s separate foundation bank is not available here yet.</p>}
    </section>;
}
